import './components/calculators/format';
import './components/calculators/input';
import './components/calculators/road-tax';

// =====================================================================
//  ROAD TAX (JPJ cukai jalan) — interactivity layer
//  Static markup lives in page-calculator-road-tax.php.
//  Rate tables come from the page as DB-editable JSON (Calc.data.roadTax).
// =====================================================================
(function () {
  const fmt = Calc.format;

  // ---- JPJ rate tables — supplied by the page from a DB-editable JSON field
  // (Calc.data.roadTax, see page-calculator-road-tax.php / calculator-road-tax.php).
  // Combustion bracket: {lo, max, base, rate} — annual = base + rate*(cc-(lo-1))
  // for progressive brackets (rate>0). EV band: {lo, max, base, inc, flatTo} —
  // stepped, see taxEV(). `max: null` means no upper bound (top bracket).
  const RATES = (window.Calc && Calc.data && Calc.data.roadTax) ? Calc.data.roadTax : { cc: {}, ev: [] };
  const cap = (m) => (m === null || m === undefined) ? Infinity : m;

  // JPJ prices EV power in blocks of 9.999 kW — i.e. 10 kW steps.
  const EV_BLOCK = 10;

  // The band's base rate covers everything up to `flatTo` (default lo+10); each
  // STARTED 10 kW block beyond that adds `inc`. Math.ceil gives exactly that
  // "started block" semantics: 220 kW -> 0 blocks, 220.001 kW -> 1 block.
  // Deliberately a step function, not an interpolation — JPJ does not prorate.
  function taxEV(kw) {
    const bands = Array.isArray(RATES.ev) ? RATES.ev : [];
    if (!bands.length) return { base: 0, prog: 0, total: 0, band: null };
    const b = bands.find(x => kw <= cap(x.max)) || bands[bands.length - 1];
    const flatTo = (b.flatTo === null || b.flatTo === undefined) ? b.lo + EV_BLOCK : b.flatTo;
    const blocks = Math.max(0, Math.ceil((kw - flatTo) / EV_BLOCK));
    const prog = blocks * (b.inc || 0);
    return { base: b.base, prog, total: b.base + prog, band: b };
  }

  // Reference axes.
  const REF_CC = [1000, 1300, 1500, 1600, 1800, 2000, 2500, 3000];
  const REF_KW = [50, 100, 150, 200, 250, 300];

  const state = {
    fuel: 'combustion',   // 'combustion' | 'electric'
    region: 'peninsular', // 'peninsular' | 'east'
    vclass: 'saloon',     // 'saloon' | 'nonsaloon'
    ownership: 'private', // 'private' | 'company'
    cc: 1500,
    kw: 150,
    activeTab: 'breakdown',
  };

  // Units sourced from the HTML (never hard-code copy). Filled at init.
  let UNIT_CC = 'cc', UNIT_KW = 'kW';

  const setText = (id, val) => { const el = document.getElementById(id); if (el) el.textContent = val; };
  const toggleAll = (sel, hide) => document.querySelectorAll(sel).forEach(el => el.classList.toggle('d-none', hide));
  const syncInput = (id, val) => Calc.input.syncValue(id, val);
  const activeText = (sel) => (document.querySelector(sel)?.textContent || '').trim();

  // Pick the combustion table for the current region/class/ownership.
  // Bracket table + cc stepping live in Calc.roadTax, shared with car-loan.
  const currentTable = () => Calc.roadTax.table(RATES, {
    region: state.region, vclass: state.vclass, ownership: state.ownership,
  });

  function bandLabelCC(band) {
    const unit = UNIT_CC;
    if (!band) return '—';
    if (band.lo === 0) return `≤ ${fmt.number(band.max, 0)} ${unit}`;
    if (band.max === null || band.max === undefined) return `≥ ${fmt.number(band.lo, 0)} ${unit}`;
    return `${fmt.number(band.lo, 0)}–${fmt.number(band.max, 0)} ${unit}`;
  }
  function bandLabelKW(band) {
    const unit = UNIT_KW;
    if (!band) return '—';
    if (band.lo === 0) return `≤ ${fmt.number(band.max, 0)} ${unit}`;
    if (band.max === null || band.max === undefined) return `> ${fmt.number(band.lo, 0)} ${unit}`;
    return `${fmt.number(band.lo, 0)}–${fmt.number(band.max, 0)} ${unit}`;
  }

  function categoryText() {
    if (state.fuel === 'electric') return activeText('#rt-fuel-tabs .btn-primary');
    const region = activeText('#rt-region-tabs .btn-primary');
    const vclass = activeText('#rt-class-tabs .btn-primary');
    if (state.vclass === 'nonsaloon') return `${region} · ${vclass}`;
    const ownership = activeText('#rt-ownership-tabs .btn-primary');
    return `${region} · ${vclass} · ${ownership}`;
  }

  function compute() {
    if (state.fuel === 'electric') {
      const r = taxEV(state.kw);
      return { ...r, engine: `${fmt.number(state.kw, 0)} ${UNIT_KW}`, bandLabel: bandLabelKW(r.band) };
    }
    const r = Calc.roadTax.cc(currentTable(), state.cc);
    return { ...r, engine: `${fmt.number(state.cc, 0)} ${UNIT_CC}`, bandLabel: bandLabelCC(r.band) };
  }

  function updateStatBand() {
    const c = compute();
    setText('rt-stat-annual',   fmt.money(c.total, 0));
    setText('rt-stat-halfyear', fmt.money(c.total / 2, 0));
    setText('rt-stat-band',     c.bandLabel);
    setText('rt-stat-category', categoryText());
  }

  function updateBreakdown() {
    const c = compute();
    setText('rt-r-category',    categoryText());
    setText('rt-r-engine',      c.engine);
    setText('rt-r-band',        c.bandLabel);
    setText('rt-r-base',        fmt.money(c.base));
    setText('rt-r-progressive', fmt.money(c.prog));
    setText('rt-r-annual',      fmt.money(c.total));
    setText('rt-r-halfyear',    fmt.money(c.total / 2));
  }

  function updateReferenceGrid() {
    if (state.fuel === 'electric') {
      const tbody = document.getElementById('rt-grid-kw');
      if (!tbody) return;
      tbody.innerHTML = REF_KW.map(kw => {
        const hi = kw === state.kw;
        return `<tr class="${hi ? 'highlight' : ''}"><th>${fmt.number(kw, 0)}</th><td>${fmt.number(taxEV(kw).total, 0)}</td></tr>`;
      }).join('');
      return;
    }
    const tbody = document.getElementById('rt-grid-cc');
    if (!tbody) return;
    const r = (RATES.cc && RATES.cc[state.region]) || {};
    // Which column is "current"? saloon+private→1, saloon+company→2, nonsaloon→3.
    const curCol = state.vclass === 'nonsaloon' ? 3 : (state.ownership === 'company' ? 2 : 1);
    tbody.innerHTML = REF_CC.map(cc => {
      const hiRow = cc === state.cc;
      const vals = [
        Calc.roadTax.cc(r.saloonPrivate, cc).total,
        Calc.roadTax.cc(r.saloonCompany || r.saloonPrivate, cc).total,
        Calc.roadTax.cc(r.nonsaloon, cc).total,
      ];
      const cells = vals.map((v, i) => `<td class="${(i + 1) === curCol ? 'highlight-col' : ''}">${fmt.number(v, 0)}</td>`).join('');
      return `<tr class="${hiRow ? 'highlight' : ''}"><th>${fmt.number(cc, 0)}</th>${cells}</tr>`;
    }).join('');
  }

  function setActive(groupSel, dataAttr, value) {
    document.querySelectorAll(`${groupSel} button`).forEach(btn => {
      const active = btn.dataset[dataAttr] === value;
      btn.classList.toggle('btn-primary', active);
      btn.classList.toggle('btn-outline-primary', !active);
    });
  }

  function refresh() {
    const isEV = state.fuel === 'electric';
    // Show cc vs kW label/unit + combustion-only controls.
    toggleAll('.rt-cc-only', isEV);
    toggleAll('.rt-kw-only', !isEV);
    toggleAll('.rt-combustion-only', isEV);
    toggleAll('.rt-electric-only', !isEV);

    // Sync the numeric input to the value for the current fuel.
    syncInput('rt-engine-input', isEV ? state.kw : state.cc);

    // Active-button styling.
    setActive('#rt-fuel-tabs', 'fuel', state.fuel);
    setActive('#rt-region-tabs', 'region', state.region);
    setActive('#rt-class-tabs', 'class', state.vclass);
    setActive('#rt-ownership-tabs', 'ownership', state.ownership);

    updateStatBand();
    if (state.activeTab === 'breakdown') updateBreakdown();
    else updateReferenceGrid();
  }

  function activateTab(tabId) {
    state.activeTab = tabId;
    document.querySelectorAll('#rt-tabs .btn-tab').forEach(btn => {
      btn.classList.toggle('active', btn.dataset.tab === tabId);
    });
    document.querySelectorAll('.tab-panel').forEach(panel => {
      panel.classList.toggle('d-none', panel.id !== `rt-tab-${tabId}`);
    });
    if (tabId === 'breakdown') updateBreakdown();
    else updateReferenceGrid();
  }

  function bindEvents() {
    ['input', 'change'].forEach(evt => document.addEventListener(evt, (e) => {
      if (e.target.id === 'rt-engine-input') {
        const v = Calc.input.getValue(e.target) || 0;
        if (state.fuel === 'electric') state.kw = v; else state.cc = v;
        refresh();
      }
    }));

    document.querySelectorAll('#rt-fuel-tabs button').forEach(btn =>
      btn.addEventListener('click', () => { state.fuel = btn.dataset.fuel; refresh(); }));
    document.querySelectorAll('#rt-region-tabs button').forEach(btn =>
      btn.addEventListener('click', () => { state.region = btn.dataset.region; refresh(); }));
    document.querySelectorAll('#rt-class-tabs button').forEach(btn =>
      btn.addEventListener('click', () => { state.vclass = btn.dataset.class; refresh(); }));
    document.querySelectorAll('#rt-ownership-tabs button').forEach(btn =>
      btn.addEventListener('click', () => { state.ownership = btn.dataset.ownership; refresh(); }));

    document.querySelectorAll('#rt-tabs .btn-tab').forEach(btn =>
      btn.addEventListener('click', () => activateTab(btn.dataset.tab)));
  }

  document.addEventListener('DOMContentLoaded', () => {
    const ccUnit = document.querySelector('.input-group-text .rt-cc-only');
    const kwUnit = document.querySelector('.input-group-text .rt-kw-only');
    if (ccUnit && ccUnit.textContent.trim()) UNIT_CC = ccUnit.textContent.trim();
    if (kwUnit && kwUnit.textContent.trim()) UNIT_KW = kwUnit.textContent.trim();
    bindEvents();
    refresh();
  });
})();
